import React, { useEffect, useRef, useState } from "react";
import NewSiteForm from "../NewSiteForm";
import ImportProjectsModal from "./ImportProjectsModal";
import sortIcon from "../../../assets/sort-desc.svg";
import styles from "./Projects.module.css";
import { BASE_URL } from "../../../utils/constant";
import { FaPlus } from "react-icons/fa";
import { MdOutlineArchive, MdOutlineUnarchive } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import RequireAccess from "components/RequireAccess";
import { ProjectApi } from "api/projects/service";
import ProjectEmptyIcon from "assets/empty state/NoProject.svg";
import LoadingScreen from "components/UI_components/LoadingScreen";
import {
  formatDateLong,
  formatCurrency,
  getRecentProjectIds,
  recordProjectAccess,
} from "../../../utils/utilityFunctions";
import Button from "components/ui-custom/Button";
import { Tabs, TabsList, TabsTrigger } from "components/ui-custom/Tabs";
import { Building2, MapPin, Upload } from "lucide-react";

const statusStyles = {
  Ongoing: {
    background: "#FEF3C7",
    color: "#B45309",
    borderColor: "#FCD34D",
  },
  Completed: {
    background: "#DCFCE7",
    color: "#166534",
    borderColor: "#86EFAC",
  },
  Halted: {
    background: "#FEE2E2",
    color: "#B91C1C",
    borderColor: "#FECACA",
  },
  Upcoming: {
    background: "#E0F2FE",
    color: "#0369A1",
    borderColor: "#7DD3FC",
  },
  "Not Started": {
    background: "#E2E8F0",
    color: "#334155",
    borderColor: "#CBD5E1",
  },
};

const getOrganizationId = () => {
  try {
    return JSON.parse(localStorage.getItem("organization"))?.id;
  } catch {
    return null;
  }
};

const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const hasNumericValue = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));

const normalizeProjectStatus = (status) => {
  if (!status) return "Upcoming";

  const normalizedStatus = String(status).trim().toLowerCase();
  if (normalizedStatus === "complete") return "Completed";
  if (normalizedStatus === "paused") return "Halted";
  if (normalizedStatus === "not started") return "Upcoming";

  return status;
};

const normalizeProject = (project) => {
  const id = project?.id ?? project?.site_id ?? null;
  const members = Array.isArray(project?.members)
    ? project.members
    : Array.isArray(project?.users)
      ? project.users
      : [];
  const estimatedBudget = hasNumericValue(project?.estimated_budget)
    ? Number(project.estimated_budget)
    : hasNumericValue(project?.esrimated_cost)
      ? Number(project.esrimated_cost)
      : null;
  const totalExpenses = hasNumericValue(project?.total_expenses)
    ? Number(project.total_expenses)
    : hasNumericValue(project?.total_expense)
      ? Number(project.total_expense)
      : 0;
  const status = normalizeProjectStatus(
    project?.status || project?.constructionStatus,
  );

  return {
    ...project,
    id,
    site_id: project?.site_id ?? id,
    name: project?.name || project?.site_name || "Untitled Project",
    site_name: project?.site_name || project?.name || "Untitled Project",
    users: members,
    members,
    active_flag: project?.active_flag !== false,
    estimated_budget: estimatedBudget,
    esrimated_cost: project?.esrimated_cost ?? estimatedBudget,
    total_expenses: totalExpenses,
    total_expense: project?.total_expense ?? totalExpenses,
    total_tasks: toNumber(project?.total_tasks),
    completed_tasks: toNumber(project?.completed_tasks),
    overdue_tasks: toNumber(project?.overdue_tasks),
    pending_subtask_updates: toNumber(project?.pending_subtask_updates),
    total_workers: toNumber(project?.total_workers),
    today_present: toNumber(project?.today_present),
    handover_date: project?.handover_date || null,
    status,
    constructionStatus: status,
    image: project?.image || project?.image_file || null,
    image_file: project?.image_file || project?.image || null,
  };
};

const normalizeProjects = (projectList) =>
  Array.isArray(projectList) ? projectList.map(normalizeProject) : [];

const readStoredProjects = () => {
  try {
    return normalizeProjects(
      JSON.parse(localStorage.getItem("projects")) || [],
    );
  } catch {
    return [];
  }
};

const persistProjects = (projectList) => {
  localStorage.setItem("projects", JSON.stringify(projectList));
  window._projects = projectList;
};

const syncProjects = (projectList, setProjects) => {
  setProjects(projectList);
  persistProjects(projectList);
};

const getProjectLocation = (project) => {
  const locationParts = [
    project?.address,
    project?.city,
    project?.state,
    project?.pincode ? `${project.pincode}` : null,
  ].filter(Boolean);

  return locationParts.length > 0
    ? locationParts.join(", ")
    : "Location details not added";
};

const getMediaUrl = (path) => {
  if (!path) return null;
  if (/^(https?:)?\/\//i.test(path) || /^data:/i.test(path)) return path;

  const sanitizedBaseUrl = BASE_URL.replace(/\/$/, "");
  const sanitizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${sanitizedBaseUrl}${sanitizedPath}`;
};

const formatMoney = (value, fallback = "Not set") =>
  hasNumericValue(value) ? `₹ ${formatCurrency(Number(value))}` : fallback;

const formatDisplayDate = (value, fallback = "Not set") => {
  if (!value) return fallback;

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? fallback : formatDateLong(value);
};

const getProgressStats = (project) => {
  const totalTasks = toNumber(project?.total_tasks);
  const completedTasks = Math.min(
    toNumber(project?.completed_tasks),
    totalTasks || toNumber(project?.completed_tasks),
  );
  const progressPercent =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return {
    progressPercent,
  };
};

const getMemberInitials = (member) => {
  const firstName =
    member?.user_details?.first_name || member?.first_name || "";
  const lastName = member?.user_details?.last_name || member?.last_name || "";
  const fallbackName =
    member?.user_details?.username ||
    member?.username ||
    member?.joined_as ||
    "M";
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim();

  return initials || fallbackName.charAt(0).toUpperCase();
};

async function fetchProjectSummaries({
  organizationId,
  showLoader,
  setLoading,
  setProjects,
}) {
  if (showLoader) {
    setLoading(true);
  }

  const accessToken = localStorage.getItem("access");
  if (!organizationId || !accessToken) {
    setLoading(false);
    return;
  }

  try {
    const response = await ProjectApi.getProjectSummaries(organizationId);
    if (!response?.status) {
      syncProjects([], setProjects);
      setLoading(false);
      return;
    }

    const normalizedProjects = normalizeProjects(response.data || []);
    syncProjects(normalizedProjects, setProjects);
  } catch (error) {
    console.error("Error fetching projects:", error);
    syncProjects([], setProjects);
  } finally {
    setLoading(false);
  }
}

export default function Projects({
  organizationId: organizationIdProp,
  currentMember,
}) {
  const canEditFinance = Boolean(
    currentMember?.finance_control || currentMember?.is_admin,
  );
  const canViewEstimatedBudget =
    canEditFinance || currentMember?.joined_as === "owner";
  const canManageProjects =
    currentMember?.project_control === true ||
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true;

  const navigate = useNavigate();
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showImportProjectsModal, setShowImportProjectsModal] = useState(false);
  const [projects, setProjects] = useState(readStoredProjects);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Recent");
  const [sortDropdown, setSortDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [archiveLoadingId, setArchiveLoadingId] = useState(null);
  const sortRef = useRef();
  const showInitialLoaderRef = useRef(projects.length === 0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");
  };

  const toggleArchiveStatus = async (site) => {
    const accessToken = localStorage.getItem("access");
    if (!accessToken) return;

    setArchiveLoadingId(site.id);
    try {
      const res = await ProjectApi.updateSiteArchiveStatus({
        siteId: site.id,
        activeFlag: !site.active_flag,
      });

      if (res.status) {
        setProjects((prevProjects) => {
          const updatedProjects = prevProjects.map((project) =>
            project.id === site.id
              ? { ...project, active_flag: !site.active_flag }
              : project,
          );
          persistProjects(updatedProjects);
          return updatedProjects;
        });
      } else {
        console.error("Failed to update site archive status");
      }
    } catch (error) {
      console.error("Error updating site archive status:", error);
    } finally {
      setArchiveLoadingId(null);
    }
  };

  const refreshProjects = (showLoader = false) =>
    fetchProjectSummaries({
      organizationId: organizationIdProp || getOrganizationId(),
      showLoader,
      setLoading,
      setProjects,
    });

  useEffect(() => {
    fetchProjectSummaries({
      organizationId: organizationIdProp || getOrganizationId(),
      showLoader: showInitialLoaderRef.current,
      setLoading,
      setProjects,
    });
    showInitialLoaderRef.current = false;
  }, [organizationIdProp]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  let filteredProjects = projects.filter(
    (project) =>
      (activeTab === "archived"
        ? project.active_flag === false
        : project.active_flag !== false) &&
      (project.name || project.site_name || "")
        .toLowerCase()
        .includes(search.toLowerCase()),
  );

  if (sort === "Name") {
    filteredProjects = [...filteredProjects].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  } else if (sort === "Budget") {
    filteredProjects = [...filteredProjects].sort(
      (a, b) => toNumber(b.estimated_budget) - toNumber(a.estimated_budget),
    );
  } else if (sort === "Recent") {
    const recentIds = getRecentProjectIds().map((id) => String(id));
    filteredProjects = [...filteredProjects].sort((a, b) => {
      const aIndex = recentIds.indexOf(String(a.id));
      const bIndex = recentIds.indexOf(String(b.id));

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
  }

  if (!organizationIdProp && !getOrganizationId()) {
    return <OrganizationEmptyState />;
  }

  if (!loading && projects.length === 0 && !canManageProjects) {
    return (
      <RequireAccess
        module="Projects"
        currentMember={currentMember}
        permission="project_control"
      />
    );
  }

  return (
    <div className="px-10 pt-8 pb-0" style={{ flex: 1 }}>
      <LoadingScreen loading={loading}>
        <div className="mb-6 space-y-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
            <p className="text-sm text-muted-foreground">
              Track delivery, budget, approvals, workforce, and team coverage
              across every project.
            </p>
          </div>
        </div>

        <div className="mb-6">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="h-9 p-1">
              <TabsTrigger value="active" className="h-7 gap-1.5 px-4 text-sm">
                Active Projects
              </TabsTrigger>
              <TabsTrigger
                value="archived"
                className="h-7 gap-1.5 px-4 text-sm"
              >
                Archived Projects
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className={styles.toolbarContainer}>
          <div className={styles.toolbarLeft}>
            <span className="text-sm font-medium text-[#737373]">
              {filteredProjects.length}{" "}
              {activeTab === "archived" ? "Archived" : "Active"} Project
              {filteredProjects.length !== 1 ? "s" : ""}
            </span>

            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search projects"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className={styles.toolbarRight}>
            <div className={styles.sortDropdown} ref={sortRef}>
              <button
                className={styles.sortBtn}
                onClick={() => setSortDropdown((current) => !current)}
                type="button"
              >
                <img
                  src={sortIcon}
                  alt="sort"
                  style={{ width: 20, height: 20 }}
                />
                Sorted by {sort}
              </button>
              {sortDropdown && (
                <div className={styles.dropdownMenu}>
                  {["Recent", "Budget", "Name"].map((option) => (
                    <div
                      key={option}
                      className={styles.dropdownItem}
                      onClick={() => {
                        setSort(option);
                        setSortDropdown(false);
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      style={{
                        color: sort === option ? "#3B0069" : "#333",
                        fontWeight: sort === option ? 600 : 400,
                      }}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {canManageProjects && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowImportProjectsModal(true)}
                  className="h-9 text-sm gap-2 border-border/60"
                >
                  <Upload className="h-4 w-4" />
                  Import From Excel
                </Button>
                <Button
                  className="flex items-center gap-2 bg-[#3B0069] text-white px-2.5 py-1.5 rounded-lg text-sm hover:!bg-purple-700 transition-colors duration-200"
                  onClick={() => setShowProjectForm(true)}
                >
                  <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                    <FaPlus className="w-3 h-3" />
                  </span>
                  <span>New Project</span>
                </Button>
              </>
            )}
          </div>
        </div>

        <div className={styles.projectsTable}>
          {filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <img
                src={ProjectEmptyIcon}
                alt="No Projects"
                className="mb-4"
                width={240}
              />
              <span className="text-2xl text-gray-500">No Projects</span>
              <span className="text-sm text-gray-500">
                {activeTab === "archived"
                  ? "Archived projects will appear here"
                  : "Add a project to get started"}
              </span>
            </div>
          ) : (
            filteredProjects.map((project) => {
              const members = project.users || [];
              const location = getProjectLocation(project);
              const status = normalizeProjectStatus(project.status);
              const statusStyle =
                statusStyles[status] || statusStyles["Not Started"];
              const projectImage = getMediaUrl(
                project.image || project.image_file,
              );
              const budget = project.estimated_budget;
              const actualExpense =
                project.total_expenses ?? project.total_expense;
              const totalTasks = toNumber(project.total_tasks);
              const handoverDate = formatDisplayDate(project.handover_date);
              const approvals = toNumber(project.pending_subtask_updates);
              const workers = toNumber(project.total_workers);
              const presentWorkers = toNumber(project.today_present);
              // const progress = getProgressStats(project);
              const progress = project.progress;
              const maxAvatars = 3;
              const extraMembers =
                members.length > maxAvatars ? members.length - maxAvatars : 0;
              const isExpenseOverBudget =
                hasNumericValue(budget) &&
                Number(actualExpense) > Number(budget);
              const workerValue =
                workers > 0
                  ? `${presentWorkers.toLocaleString("en-IN")}/${workers.toLocaleString("en-IN")}`
                  : "0";
              const expenseClassName = isExpenseOverBudget
                ? styles.metricValueDanger
                : hasNumericValue(actualExpense)
                  ? styles.metricValueSuccess
                  : styles.metricValueNeutral;

              return (
                <div
                  key={project.id}
                  className={`${styles.projectCard} ${styles.compactProjectCard}`}
                  onClick={() => {
                    recordProjectAccess(project.id);
                    localStorage.setItem("site", project.id);
                    localStorage.setItem(
                      "siteName",
                      project.site_name || project.name,
                    );
                    localStorage.setItem("siteData", JSON.stringify(project));
                    navigate(`/site/${project.id}/home`);
                  }}
                >
                  <div className={styles.compactTopRow}>
                    <div className={styles.projectIdentity}>
                      <div className={styles.projectMedia}>
                        {projectImage ? (
                          <img
                            src={projectImage}
                            alt={project.site_name || project.name}
                          />
                        ) : (
                          <div className={styles.projectMediaFallback}>
                            <Building2 className="h-6 w-6" />
                          </div>
                        )}
                      </div>

                      <div className={styles.projectIdentityText}>
                        <div className={styles.projectTitleRow}>
                          <h3 className={styles.projectTitle}>
                            {project.name || "Project Name"}
                          </h3>
                          <span
                            className={styles.statusBadge}
                            style={statusStyle}
                          >
                            {status}
                          </span>
                        </div>
                        <div className={styles.projectLocationRow}>
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                          <p className={styles.locationText}>{location}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row items-center justify-between pr-4">
                      {canViewEstimatedBudget && (
                        <div
                          className={`${styles.compactMetric} ${styles.metricEstimated}`}
                        >
                          <div className={styles.compactMetricLabel}>
                            Estimated
                          </div>
                          <div className={styles.compactMetricValue}>
                            {formatMoney(budget, "Not set")}
                          </div>
                        </div>
                      )}
                      {canViewEstimatedBudget && (
                        <div
                          className={`${styles.compactMetric} ${styles.metricExpenses}`}
                        >
                          <div className={styles.compactMetricLabel}>
                            Expenses
                          </div>
                          <div
                            className={`${styles.compactMetricValue} ${expenseClassName}`}
                          >
                            {formatMoney(actualExpense, "₹ 0")}
                          </div>
                        </div>
                      )}
                      {canManageProjects && (
                        <div
                          className={`${styles.compactMetric} ${styles.metricApprovals}`}
                        >
                          <div className={styles.compactMetricLabel}>
                            Approvals
                          </div>
                          <div className={styles.compactMetricValue}>
                            {approvals.toLocaleString("en-IN")}
                          </div>
                        </div>
                      )}
                      <div
                        className={`${styles.compactMetric} ${styles.metricWorkers}`}
                      >
                        <div className={styles.compactMetricLabel}>Workers</div>
                        <div className={styles.compactMetricValue}>
                          {workerValue}
                        </div>
                      </div>
                      <div
                        className={`${styles.compactMetric} ${styles.metricHandover}`}
                      >
                        <div className={styles.compactMetricLabel}>
                          Handover Date
                        </div>
                        <div className={styles.compactMetricValue}>
                          {handoverDate}
                        </div>
                      </div>
                      {/* <div
                        className={`${styles.compactMetric} ${styles.metricTasks}`}
                      >
                        <div className={styles.compactMetricLabel}>
                          Total Tasks
                        </div>
                        <div className={styles.compactMetricValue}>
                          {totalTasks.toLocaleString("en-IN")}
                        </div>
                      </div> */}
                    </div>
                    <div className={styles.projectStatsRow}>
                      <div className={styles.membersColumn}>
                        {members.length > 0 ? (
                          <div className={styles.membersInline}>
                            <div className={styles.memberStack}>
                              {members
                                .slice(0, maxAvatars)
                                .map((member, index) => {
                                  const memberImage = getMediaUrl(
                                    member?.user_details?.userpic ||
                                      member?.userpic ||
                                      member?.user?.userpic,
                                  );

                                  return memberImage ? (
                                    <img
                                      key={member?.id || index}
                                      src={memberImage}
                                      alt={
                                        member?.user_details?.first_name ||
                                        "Member"
                                      }
                                      className={styles.memberAvatar}
                                    />
                                  ) : (
                                    <div
                                      key={member?.id || index}
                                      className={styles.memberFallback}
                                    >
                                      {getMemberInitials(member)}
                                    </div>
                                  );
                                })}
                              {extraMembers > 0 && (
                                <span className="flex flex-col items-center justify-center text-xs rounded-full w-7 h-7 bg-sm-primary text-white">
                                  +{extraMembers}
                                </span>
                              )}
                            </div>
                            {/* <span className={styles.membersCount}>{members.length}</span> */}
                          </div>
                        ) : (
                          <div className={styles.membersEmpty}>No members</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.compactBottomRow}>
                    <div className={styles.compactProgressWrap}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className={styles.progressPercent}>{progress}%</div>
                    </div>

                    {canManageProjects && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={styles.archiveButton}
                        disabled={archiveLoadingId === project.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (archiveLoadingId !== project.id) {
                            toggleArchiveStatus(project);
                          }
                        }}
                      >
                        {project.active_flag === false ? (
                          <MdOutlineUnarchive className="text-base" />
                        ) : (
                          <MdOutlineArchive className="text-base" />
                        )}
                        {project.active_flag === false
                          ? "Unarchive"
                          : "Archive"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </LoadingScreen>

      {showProjectForm && (
        <div className={styles.modalOverlay}>
          <NewSiteForm
            open={showProjectForm}
            onClose={() => {
              setShowProjectForm(false);
              refreshProjects(true);
            }}
            onSubmit={() => {
              setShowProjectForm(false);
              refreshProjects(true);
            }}
          />
        </div>
      )}

      {showImportProjectsModal && (
        <ImportProjectsModal
          organizationId={organizationIdProp || getOrganizationId()}
          onUploadSuccess={() => refreshProjects(true)}
          onClose={() => setShowImportProjectsModal(false)}
        />
      )}
    </div>
  );
}
